<script setup lang="ts">
import { ref } from 'vue'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import { Eye, EyeOff, AlertCircle } from 'lucide-vue-next'
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { useAuth } from '@/composables/useAuth'
import { handleAuthError } from '@/utils/auth/errorMapping'
import {
  emailOnlySchema,
  passwordSchema,
} from '@/utils/auth/validation'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'

const emit = defineEmits<{
  success: [token: string]
  error: [message: string]
  forgotPassword: []
  stepChange: [step: string]
}>()

const { signInWithEmail, checkEmailExists, isLoading: authLoading, error: authError, clearError: clearAuthError } = useAuth()

type Step = 'email' | 'signin' | 'signup'
const step = ref<Step>('email')
const enteredEmail = ref('')
const showPassword = ref(false)
const formError = ref<string | null>(null)

const isLoading = ref(false)

// Schemas for each step
const emailStepSchema = toTypedSchema(emailOnlySchema)

const signInStepSchema = toTypedSchema(z.object({
  password: passwordSchema,
}))

const signUpStepSchema = toTypedSchema(z.object({
  password: z.string().min(6, 'Password must be at least 6 characters').max(128, 'Password is too long'),
  confirmPassword: z.string().min(6, 'Please confirm your password'),
  displayName: z.string().min(1, 'Display name is required').max(255, 'Display name is too long'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}))

const onEmailSubmit = async (values: Record<string, any>) => {
  formError.value = null
  clearAuthError()
  isLoading.value = true

  try {
    const email = values.email as string
    enteredEmail.value = email
    const exists = await checkEmailExists(email)

    if (authError.value) {
      formError.value = authError.value
      return
    }

    step.value = exists ? 'signin' : 'signup'
    emit('stepChange', step.value)
  } finally {
    isLoading.value = false
  }
}

const onSignInSubmit = async (values: Record<string, any>) => {
  formError.value = null
  clearAuthError()

  const token = await signInWithEmail(enteredEmail.value, values.password as string)

  if (token) {
    formError.value = null
    emit('success', token)
  } else {
    formError.value = authError.value || 'Authentication failed'
    emit('error', formError.value)
  }
}

const onSignUpSubmit = async (values: Record<string, any>) => {
  formError.value = null
  isLoading.value = true
  try {
    const auth = getAuth()
    const result = await createUserWithEmailAndPassword(auth, enteredEmail.value, values.password)
    await updateProfile(result.user, { displayName: values.displayName })
    const token = await result.user.getIdToken()
    emit('success', token)
  } catch (err: any) {
    formError.value = handleAuthError(err, 'firebase')
    emit('error', formError.value)
  } finally {
    isLoading.value = false
  }
}

const goBackToEmail = () => {
  step.value = 'email'
  formError.value = null
  clearAuthError()
  emit('stepChange', 'email')
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}
</script>

<template>
  <!-- Step 1: Email Only -->
  <Form
    v-if="step === 'email'"
    :validation-schema="emailStepSchema"
    :initial-values="{ email: enteredEmail }"
    @submit="onEmailSubmit"
    class="space-y-4"
    :validate-on-blur="true"
    :validate-on-change="false"
    :validate-on-input="false"
    :validate-on-model-update="false"
  >
    <Alert v-if="formError" variant="destructive" class="mb-4">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ formError }}</AlertDescription>
    </Alert>

    <FormField name="email" v-slot="{ componentField }">
      <FormItem>
        <FormLabel>Email</FormLabel>
        <FormControl>
          <Input
            type="email"
            placeholder="your@email.com"
            autocomplete="email"
            v-bind="componentField"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <Button
      type="submit"
      :disabled="isLoading"
      class="w-full h-12 text-base font-medium"
    >
      <span v-if="isLoading">Checking...</span>
      <span v-else>Next</span>
    </Button>
  </Form>

  <!-- Step 2: Sign In (account exists) -->
  <Form
    v-else-if="step === 'signin'"
    :validation-schema="signInStepSchema"
    @submit="onSignInSubmit"
    class="space-y-4"
    :validate-on-blur="true"
    :validate-on-change="false"
    :validate-on-input="false"
    :validate-on-model-update="false"
  >
    <Alert v-if="formError" variant="destructive" class="mb-4">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ formError }}</AlertDescription>
    </Alert>

    <!-- Email display with change button -->
    <div class="flex items-center justify-between py-2 px-3 bg-muted rounded-md">
      <span class="text-sm text-foreground truncate">{{ enteredEmail }}</span>
      <button
        type="button"
        @click="goBackToEmail"
        class="text-sm text-primary hover:text-primary/80 hover:underline ml-2 shrink-0"
      >
        Change
      </button>
    </div>

    <FormField name="password" v-slot="{ componentField }">
      <FormItem>
        <FormLabel>Password</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              :type="showPassword ? 'text' : 'password'"
              placeholder="Enter your password"
              autocomplete="current-password"
              v-bind="componentField"
              class="pr-10"
            />
            <button
              type="button"
              @click="togglePasswordVisibility"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/80"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <Eye v-if="!showPassword" class="w-5 h-5" />
              <EyeOff v-else class="w-5 h-5" />
            </button>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div class="flex justify-end">
      <button
        type="button"
        @click="emit('forgotPassword')"
        class="text-sm text-primary hover:text-primary/80 hover:underline"
      >
        Forgot password?
      </button>
    </div>

    <Button
      type="submit"
      :disabled="authLoading"
      class="w-full h-12 text-base font-medium"
    >
      <span v-if="authLoading">Signing in...</span>
      <span v-else>Sign in</span>
    </Button>
  </Form>

  <!-- Step 3: Sign Up (new account) -->
  <Form
    v-else-if="step === 'signup'"
    :validation-schema="signUpStepSchema"
    @submit="onSignUpSubmit"
    class="space-y-4"
    :validate-on-blur="true"
    :validate-on-change="false"
    :validate-on-input="false"
    :validate-on-model-update="false"
  >
    <Alert v-if="formError" variant="destructive" class="mb-4">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ formError }}</AlertDescription>
    </Alert>

    <!-- Email display with change button -->
    <div class="flex items-center justify-between py-2 px-3 bg-muted rounded-md">
      <span class="text-sm text-foreground truncate">{{ enteredEmail }}</span>
      <button
        type="button"
        @click="goBackToEmail"
        class="text-sm text-primary hover:text-primary/80 hover:underline ml-2 shrink-0"
      >
        Change
      </button>
    </div>

    <FormField name="password" v-slot="{ componentField }">
      <FormItem>
        <FormLabel>Password</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              :type="showPassword ? 'text' : 'password'"
              placeholder="At least 6 characters"
              autocomplete="new-password"
              v-bind="componentField"
              class="pr-10"
            />
            <button
              type="button"
              @click="togglePasswordVisibility"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/80"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <Eye v-if="!showPassword" class="w-5 h-5" />
              <EyeOff v-else class="w-5 h-5" />
            </button>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField name="confirmPassword" v-slot="{ componentField }">
      <FormItem>
        <FormLabel>Confirm Password</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              :type="showPassword ? 'text' : 'password'"
              placeholder="Confirm your password"
              autocomplete="new-password"
              v-bind="componentField"
              class="pr-10"
            />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField name="displayName" v-slot="{ componentField }">
      <FormItem>
        <FormLabel>Display Name</FormLabel>
        <FormControl>
          <Input
            placeholder="John Doe"
            autocomplete="name"
            v-bind="componentField"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <Button
      type="submit"
      :disabled="isLoading"
      class="w-full h-12 text-base font-medium"
    >
      <span v-if="isLoading">Creating account...</span>
      <span v-else>Continue</span>
    </Button>
  </Form>
</template>
